import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * Sheet — right-anchored overlay sheet over a scrim (T-0140).
 *
 * Mirrors `.sheet` in `docs/requirement-library/prototype/selection.css`:
 * a wide panel (up to 1120px, 94vw) sliding in from the right, with a
 * header, a body, and a footer that holds a running summary on the left
 * and actions on the right. Used for multi-step picking flows (add from
 * library, import a spreadsheet).
 *
 * Built on Radix Dialog like the existing drawers (for example
 * `contract/deliverable-drawer.tsx`), which provides the focus trap,
 * focus return, Esc to close and `role="dialog"`. Scrim click closes by
 * default; pass `closeOnScrimClick={false}` for flows where a stray
 * click would lose work.
 */

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  /** Line under the title. */
  description?: React.ReactNode;
  /** Extra header content, right of the title and left of close. */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  /** Footer left: running summary (counts, totals). */
  summary?: React.ReactNode;
  /** Footer right: action buttons. */
  actions?: React.ReactNode;
  /** Default true. False keeps the sheet open on scrim click. */
  closeOnScrimClick?: boolean;
  /** Width utility override. Default `w-[min(1120px,94vw)]`. */
  widthClassName?: string;
  /**
   * Called before Esc closes the sheet. `preventDefault()` keeps it open,
   * for example while a control inside the sheet has its own popup open.
   */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  className?: string;
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  headerActions,
  children,
  summary,
  actions,
  closeOnScrimClick = true,
  widthClassName = 'w-[min(1120px,94vw)]',
  onEscapeKeyDown,
  className,
}: SheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay data-testid="sheet-scrim" className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          // No description: tell Radix so explicitly rather than leave a
          // dangling aria-describedby.
          {...(description ? {} : { 'aria-describedby': undefined })}
          onInteractOutside={(e) => {
            if (!closeOnScrimClick) e.preventDefault();
          }}
          onEscapeKeyDown={onEscapeKeyDown}
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex flex-col bg-paper-0 shadow-modal focus:outline-none',
            widthClassName,
            className,
          )}
        >
          <div className="flex flex-none items-center gap-3 border-b border-ink-100 px-5 py-4">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-h2 font-semibold text-ink-900">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-0.5 text-meta font-medium text-ink-500">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            {headerActions}
            <Dialog.Close
              aria-label="Close"
              className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-card border border-ink-200 text-ink-700 hover:bg-paper-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <div className="flex min-h-0 flex-1">{children}</div>
          {summary || actions ? (
            <div className="flex flex-none items-center gap-3 border-t border-ink-100 bg-paper-50 px-5 py-3.5">
              <div className="flex min-w-0 flex-1 flex-wrap gap-4 text-body-sm text-ink-700">
                {summary}
              </div>
              {actions ? <div className="flex flex-none items-center gap-2">{actions}</div> : null}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

Sheet.displayName = 'Sheet';

/** A bold tabular figure for the sheet summary: `<SheetStat value={12} label="selected" />`. */
export function SheetStat({ value, label }: { value: React.ReactNode; label: React.ReactNode }) {
  return (
    <span>
      <b className="font-semibold tabular-nums text-ink-900">{value}</b> {label}
    </span>
  );
}
